import React, { useState, useEffect } from 'react';
import {
  X,
  Webhook,
  Save,
  AlertTriangle,
  CheckCircle,
  Info,
  Plus,
  Trash2,
  Eye,
  EyeOff,
  Play,
  Loader2,
  ChevronDown,
  ChevronRight,
  Settings,
  Code,
  Link,
  Key,
  Clock,
  RefreshCw,
  AlertCircle,
  Copy,
  Check,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// HTTP Methods
const HTTP_METHODS = [
  { value: 'POST', label: 'POST', color: 'green' },
  { value: 'GET', label: 'GET', color: 'blue' },
  { value: 'PUT', label: 'PUT', color: 'orange' },
  { value: 'PATCH', label: 'PATCH', color: 'yellow' },
  { value: 'DELETE', label: 'DELETE', color: 'red' },
];

// Variable tokens for interpolation
const VARIABLE_TOKENS = [
  { category: 'Workflow', tokens: [
    { token: '{{workflow_id}}', label: 'Workflow ID' },
    { token: '{{workflow_name}}', label: 'Workflow Name' },
    { token: '{{run_id}}', label: 'Run ID' },
    { token: '{{execution_id}}', label: 'Execution ID' },
    { token: '{{timestamp}}', label: 'Timestamp (ISO)' },
  ]},
  { category: 'Contact', tokens: [
    { token: '{{contact.first_name}}', label: 'First Name' },
    { token: '{{contact.last_name}}', label: 'Last Name' },
    { token: '{{contact.email}}', label: 'Email' },
    { token: '{{contact.phone}}', label: 'Phone' },
    { token: '{{contact_id}}', label: 'Contact ID' },
  ]},
  { category: 'Trigger', tokens: [
    { token: '{{trigger_type}}', label: 'Trigger Type' },
    { token: '{{trigger.event}}', label: 'Trigger Event' },
    { token: '{{trigger.data}}', label: 'Trigger Data' },
  ]},
  { category: 'Context', tokens: [
    { token: '{{agency_id}}', label: 'Agency ID' },
    { token: '{{user_id}}', label: 'User ID' },
  ]},
];

// Default body template
const DEFAULT_BODY_TEMPLATE = `{
  "event": "workflow_action",
  "workflow_id": "{{workflow_id}}",
  "run_id": "{{run_id}}",
  "timestamp": "{{timestamp}}",
  "contact": {
    "id": "{{contact_id}}",
    "email": "{{contact.email}}",
    "name": "{{contact.first_name}} {{contact.last_name}}"
  },
  "trigger": {
    "type": "{{trigger_type}}"
  }
}`;

const WebhookNodeEditor = ({ 
  step, 
  onSave, 
  onClose, 
  agencyId,
  getAuthHeaders 
}) => {
  const [config, setConfig] = useState({
    url: '',
    method: 'POST',
    headers: [],
    body_template: DEFAULT_BODY_TEMPLATE,
    content_type: 'application/json',
    timeout_seconds: 30,
    max_retries: 3,
    retry_on_5xx: true,
    retry_on_network_error: true,
    include_idempotency_header: true,
    ...step?.config
  });
  
  const [testResult, setTestResult] = useState(null);
  const [isTesting, setIsTesting] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showVariables, setShowVariables] = useState(false);
  const [copiedToken, setCopiedToken] = useState(null);
  const [showSecretValues, setShowSecretValues] = useState({});
  const [errors, setErrors] = useState({});

  // Validate URL
  const validateUrl = (url) => {
    if (!url) return 'URL is required';
    try {
      const parsed = new URL(url.startsWith('{{') ? 'https://placeholder.com' : url);
      if (!['http:', 'https:'].includes(parsed.protocol)) {
        return 'Only HTTP and HTTPS URLs are allowed';
      }
    } catch {
      if (!url.includes('{{')) {
        return 'Invalid URL format';
      }
    }
    return null;
  };

  // Validate config
  useEffect(() => {
    const newErrors = {};
    const urlError = validateUrl(config.url);
    if (urlError) newErrors.url = urlError;
    
    // Validate headers
    config.headers.forEach((h, i) => {
      if (!h.key?.trim()) {
        newErrors[`header_${i}_key`] = 'Header name required';
      }
    });
    
    setErrors(newErrors);
  }, [config]);

  const isValid = Object.keys(errors).length === 0 && config.url;

  // Add header
  const addHeader = () => {
    setConfig(prev => ({
      ...prev,
      headers: [...prev.headers, { key: '', value: '', is_secret: false }]
    }));
  };

  // Update header
  const updateHeader = (index, field, value) => {
    setConfig(prev => ({
      ...prev,
      headers: prev.headers.map((h, i) => 
        i === index ? { ...h, [field]: value } : h
      )
    }));
  };

  // Remove header
  const removeHeader = (index) => {
    setConfig(prev => ({
      ...prev,
      headers: prev.headers.filter((_, i) => i !== index)
    }));
  };

  // Toggle secret visibility
  const toggleSecretVisibility = (index) => {
    setShowSecretValues(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Copy token to clipboard
  const copyToken = (token) => {
    navigator.clipboard.writeText(token);
    setCopiedToken(token);
    setTimeout(() => setCopiedToken(null), 2000);
  };

  // Insert token into body
  const insertToken = (token) => {
    setConfig(prev => ({
      ...prev,
      body_template: prev.body_template + token
    }));
  };

  // Test webhook
  const testWebhook = async () => {
    setIsTesting(true);
    setTestResult(null);
    
    try {
      const response = await fetch(`${API_URL}/api/workflows/${agencyId}/webhook/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          url: config.url,
          method: config.method,
          headers: config.headers,
          body_template: config.body_template,
          content_type: config.content_type,
          timeout_seconds: config.timeout_seconds,
          include_idempotency_header: config.include_idempotency_header,
          test_contact_data: {
            first_name: 'Test',
            last_name: 'User',
            email: 'test@example.com',
            phone: '+1234567890'
          }
        })
      });
      
      const result = await response.json();
      setTestResult(result);
    } catch (error) {
      setTestResult({
        success: false,
        error: error.message || 'Test failed'
      });
    } finally {
      setIsTesting(false);
    }
  };

  // Save configuration
  const handleSave = () => {
    if (!isValid) return;
    
    onSave({
      ...step,
      config: {
        ...config,
        configured: true
      }
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between bg-gray-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
              <Webhook className="w-5 h-5 text-gray-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Configure Webhook</h2>
              <p className="text-sm text-gray-500">Call an external URL when this step executes</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* URL & Method */}
          <div className="space-y-4">
            <div className="flex gap-3">
              {/* Method Selector */}
              <div className="w-32">
                <Label className="text-sm font-medium text-gray-700 mb-1.5 block">Method</Label>
                <select
                  value={config.method}
                  onChange={(e) => setConfig(prev => ({ ...prev, method: e.target.value }))}
                  className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                >
                  {HTTP_METHODS.map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>
              
              {/* URL Input */}
              <div className="flex-1">
                <Label className="text-sm font-medium text-gray-700 mb-1.5 block">
                  Webhook URL <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={config.url}
                  onChange={(e) => setConfig(prev => ({ ...prev, url: e.target.value }))}
                  placeholder="https://api.example.com/webhook"
                  className={cn(errors.url && "border-red-500 focus:ring-red-500")}
                />
                {errors.url && (
                  <p className="text-xs text-red-500 mt-1">{errors.url}</p>
                )}
              </div>
            </div>
            
            <p className="text-xs text-gray-500 flex items-center gap-1">
              <Info className="w-3 h-3" />
              URL can include variables like <code className="bg-gray-100 px-1 rounded">{'{{contact.email}}'}</code>
            </p>
          </div>

          {/* Headers */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium text-gray-700">Headers</Label>
              <Button
                variant="outline"
                size="sm"
                onClick={addHeader}
                className="h-7 text-xs"
              >
                <Plus className="w-3 h-3 mr-1" />
                Add Header
              </Button>
            </div>
            
            {config.headers.length === 0 ? (
              <p className="text-sm text-gray-500 italic">No custom headers configured</p>
            ) : (
              <div className="space-y-2">
                {config.headers.map((header, index) => (
                  <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                    <Input
                      value={header.key}
                      onChange={(e) => updateHeader(index, 'key', e.target.value)}
                      placeholder="Header name"
                      className={cn("h-8 text-sm w-40", errors[`header_${index}_key`] && "border-red-500")}
                    />
                    <div className="relative flex-1">
                      <Input
                        type={header.is_secret && !showSecretValues[index] ? 'password' : 'text'}
                        value={header.value}
                        onChange={(e) => updateHeader(index, 'value', e.target.value)}
                        placeholder="Header value"
                        className="h-8 text-sm pr-8"
                      />
                      {header.is_secret && (
                        <button
                          type="button"
                          onClick={() => toggleSecretVisibility(index)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showSecretValues[index] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      )}
                    </div>
                    <label className="flex items-center gap-1.5 text-xs text-gray-600 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={header.is_secret}
                        onChange={(e) => updateHeader(index, 'is_secret', e.target.checked)}
                        className="rounded border-gray-300"
                      />
                      <Key className="w-3 h-3" />
                      Secret
                    </label>
                    <button
                      onClick={() => removeHeader(index)}
                      className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            <p className="text-xs text-gray-500 flex items-center gap-1">
              <Key className="w-3 h-3" />
              Secret headers are encrypted at rest and never shown in plaintext after save
            </p>
          </div>

          {/* Body Template (for POST/PUT/PATCH) */}
          {['POST', 'PUT', 'PATCH'].includes(config.method) && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium text-gray-700">Request Body (JSON)</Label>
                <button
                  onClick={() => setShowVariables(!showVariables)}
                  className="text-xs text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
                >
                  <Code className="w-3 h-3" />
                  {showVariables ? 'Hide' : 'Show'} Variables
                </button>
              </div>
              
              {showVariables && (
                <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                  <p className="text-xs text-gray-600 font-medium">Available Variables (click to copy)</p>
                  {VARIABLE_TOKENS.map(cat => (
                    <div key={cat.category} className="space-y-1">
                      <p className="text-xs text-gray-500">{cat.category}</p>
                      <div className="flex flex-wrap gap-1">
                        {cat.tokens.map(t => (
                          <button
                            key={t.token}
                            onClick={() => copyToken(t.token)}
                            className={cn(
                              "px-2 py-0.5 text-xs rounded border transition-colors",
                              copiedToken === t.token
                                ? "bg-emerald-100 border-emerald-300 text-emerald-700"
                                : "bg-white border-gray-200 text-gray-600 hover:border-emerald-300"
                            )}
                            title={t.label}
                          >
                            {copiedToken === t.token ? <Check className="w-3 h-3 inline mr-1" /> : null}
                            {t.token}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              <Textarea
                value={config.body_template}
                onChange={(e) => setConfig(prev => ({ ...prev, body_template: e.target.value }))}
                placeholder='{"key": "value", "contact": "{{contact.email}}"}'
                className="font-mono text-sm min-h-[200px]"
              />
            </div>
          )}

          {/* Advanced Settings */}
          <div className="border rounded-lg">
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="w-full px-4 py-3 flex items-center justify-between text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <span className="flex items-center gap-2">
                <Settings className="w-4 h-4" />
                Advanced Settings
              </span>
              {showAdvanced ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
            
            {showAdvanced && (
              <div className="px-4 pb-4 space-y-4 border-t">
                {/* Timeout */}
                <div className="pt-4">
                  <Label className="text-sm font-medium text-gray-700 mb-1.5 block">
                    Timeout (seconds)
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={config.timeout_seconds}
                      onChange={(e) => setConfig(prev => ({ ...prev, timeout_seconds: parseInt(e.target.value) || 30 }))}
                      min={1}
                      max={120}
                      className="w-24 h-8"
                    />
                    <span className="text-xs text-gray-500">1-120 seconds</span>
                  </div>
                </div>
                
                {/* Retry Configuration */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium text-gray-700">Retry Policy</Label>
                  
                  <div className="flex items-center gap-4">
                    <div>
                      <Label className="text-xs text-gray-500 mb-1 block">Max Retries</Label>
                      <Input
                        type="number"
                        value={config.max_retries}
                        onChange={(e) => setConfig(prev => ({ ...prev, max_retries: parseInt(e.target.value) || 0 }))}
                        min={0}
                        max={10}
                        className="w-20 h-8"
                      />
                    </div>
                    
                    <label className="flex items-center gap-2 text-sm text-gray-600">
                      <input
                        type="checkbox"
                        checked={config.retry_on_5xx}
                        onChange={(e) => setConfig(prev => ({ ...prev, retry_on_5xx: e.target.checked }))}
                        className="rounded border-gray-300"
                      />
                      Retry on 5xx errors
                    </label>
                    
                    <label className="flex items-center gap-2 text-sm text-gray-600">
                      <input
                        type="checkbox"
                        checked={config.retry_on_network_error}
                        onChange={(e) => setConfig(prev => ({ ...prev, retry_on_network_error: e.target.checked }))}
                        className="rounded border-gray-300"
                      />
                      Retry on network errors
                    </label>
                  </div>
                  
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <RefreshCw className="w-3 h-3" />
                    Uses exponential backoff: 1s, 2s, 4s... up to 60s
                  </p>
                </div>
                
                {/* Idempotency */}
                <label className="flex items-center gap-2 text-sm text-gray-600">
                  <input
                    type="checkbox"
                    checked={config.include_idempotency_header}
                    onChange={(e) => setConfig(prev => ({ ...prev, include_idempotency_header: e.target.checked }))}
                    className="rounded border-gray-300"
                  />
                  Include idempotency headers (X-Idempotency-Key, X-Delivery-ID)
                </label>
              </div>
            )}
          </div>

          {/* Test Result */}
          {testResult && (
            <div className={cn(
              "rounded-lg p-4 space-y-2",
              testResult.success ? "bg-emerald-50 border border-emerald-200" : "bg-red-50 border border-red-200"
            )}>
              <div className="flex items-center gap-2">
                {testResult.success ? (
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-red-600" />
                )}
                <span className={cn(
                  "font-medium",
                  testResult.success ? "text-emerald-700" : "text-red-700"
                )}>
                  {testResult.success ? 'Test Successful' : 'Test Failed'}
                </span>
                {testResult.status_code && (
                  <Badge variant={testResult.success ? "default" : "destructive"}>
                    {testResult.status_code}
                  </Badge>
                )}
                {testResult.duration_ms && (
                  <span className="text-xs text-gray-500">{testResult.duration_ms}ms</span>
                )}
              </div>
              
              {testResult.error && (
                <p className="text-sm text-red-600">{testResult.error}</p>
              )}
              
              {testResult.response_preview && (
                <div className="mt-2">
                  <p className="text-xs text-gray-500 mb-1">Response Preview:</p>
                  <pre className="text-xs bg-white/50 p-2 rounded border overflow-x-auto max-h-32">
                    {testResult.response_preview}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-between">
          <Button
            variant="outline"
            onClick={testWebhook}
            disabled={!isValid || isTesting}
            className="gap-2"
          >
            {isTesting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Play className="w-4 h-4" />
            )}
            {isTesting ? 'Testing...' : 'Test Webhook'}
          </Button>
          
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!isValid}
              className="gap-2 bg-emerald-600 hover:bg-emerald-700"
            >
              <Save className="w-4 h-4" />
              Save Configuration
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WebhookNodeEditor;
